import { useEffect, useState, useRef } from 'react';
import { Text } from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { CardScreen } from '@/components/common/CardScreen';
import LoadingScreen from '@/components/common/LoadingScreen';
import FeedbackForm from '@/components/Feedback/FeedbackForm';
import fireworks from '@/assets/fireworks.png';
import { t } from '@/strings/index';
import { logScreenRender, logFeedbackCompletion } from '@/telemetry/telemetryEvents';
import { ROUTES } from '@/constants/routes';
import '@/styles/FeedbackThanksScreen.css';

// Using Microsoft InApp Feedback SDK with Rating scenario
// Doc: https://eng.ms/docs/experiences-devices/dpg-services/release-sentiment-services/microsoft-feedback/inapp-feedback-collection/feedback-documentation/wrappers-sdks/web/overview-and-integration

export default function FeedbackThanksScreen() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const screenStartTimeRef = useRef<number>(Date.now());
  const feedbackIdRef = useRef<string | undefined>();

  useEffect(() => {
    // Log screen view (UserBI only - scenario already completed at AppDownloadScreen)
    logScreenRender('FeedbackThanksScreen');

    // Record when user entered feedback screen
    screenStartTimeRef.current = Date.now();

    // Preserve scenarioCompleted flag before clearing session storage
    // This flag prevents false abandonment tracking and duplicate scenario completions
    const scenarioCompleted = sessionStorage.getItem('scenarioCompleted');
    sessionStorage.clear();
    if (scenarioCompleted) sessionStorage.setItem('scenarioCompleted', scenarioCompleted);
    // Set flag to tell Footer to hide feedback link while inline form is active
    sessionStorage.setItem('inlineFeedbackActive', 'true');

    return () => {
      // Clean up flag when leaving the screen
      sessionStorage.removeItem('inlineFeedbackActive');
    };
  }, []);

  const handleFeedbackReady = () => {
    console.log('FeedbackThanksScreen: Feedback SDK ready');
    // FeedbackForm logs this internally now
    setIsLoading(false);
  };

  const handleFeedbackSubmitStart = () => {
    console.log('FeedbackThanksScreen: Submit clicked, showing loader');
    setIsSubmitting(true);
  };

  const handleFeedbackSuccess = (feedbackId?: string) => {
    console.log('FeedbackThanksScreen: Feedback submitted successfully, feedbackId:', feedbackId);

    // Store feedbackId for completion tracking
    feedbackIdRef.current = feedbackId;

    // Calculate time spent on feedback screen
    const timeOnScreenMs = Date.now() - screenStartTimeRef.current;

    // Log feedback completion (UserBI event)
    logFeedbackCompletion(true, 'thanks-screen', {
      wasSubmitted: true,
      wasCancelled: false,
      feedbackId,
      timeOnScreenMs,
      authenticationType: 'anonymous',
    });

    sessionStorage.removeItem('inlineFeedbackActive');
    navigate(ROUTES.FEEDBACK_SUBMITTED);
  };

  const handleFeedbackError = (error: string) => {
    console.error('FeedbackThanksScreen: Feedback submission failed:', error);

    // Calculate time spent before error
    const timeOnScreenMs = Date.now() - screenStartTimeRef.current;

    // Log feedback completion with error (UserBI event)
    logFeedbackCompletion(false, 'thanks-screen', {
      wasSubmitted: false,
      wasCancelled: false,
      timeOnScreenMs,
      authenticationType: 'anonymous',
    });

    sessionStorage.removeItem('inlineFeedbackActive');
    navigate(ROUTES.FEEDBACK_SUBMITTED);
  };

  const handleFeedbackDismiss = (wasSubmitted: boolean) => {
    console.log('FeedbackThanksScreen: Feedback dismissed, wasSubmitted:', wasSubmitted);
    // FeedbackForm logs this internally now

    // If user clicked Cancel (wasSubmitted=false), go to submitted screen
    if (!wasSubmitted) {
      // Calculate time spent before cancelling
      const timeOnScreenMs = Date.now() - screenStartTimeRef.current;

      // Log feedback completion - user cancelled (UserBI event)
      logFeedbackCompletion(false, 'thanks-screen', {
        wasSubmitted: false,
        wasCancelled: true,
        timeOnScreenMs,
        authenticationType: 'anonymous',
      });

      sessionStorage.removeItem('inlineFeedbackActive');
      navigate(ROUTES.FEEDBACK_SUBMITTED);
    }
  };

  const showLoadingOverlay = isLoading || isSubmitting;

  // Always render the same structure - use CSS to show/hide
  // This keeps the FeedbackForm instance mounted so SDK state is preserved
  return (
    <>
      {/* Loading overlay - shown during SDK init or submission */}
      {showLoadingOverlay && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}>
          <LoadingScreen />
        </div>
      )}

      {/* Card content - always mounted, hidden behind loader when needed */}
      <div style={{ visibility: showLoadingOverlay ? 'hidden' : 'visible' }}>
        <CardScreen
          title={t('feedbackThanks.title')}
          showBackButton={false}
          showUserInfo={false}
          showLogo={false}
          autoFocusPrimaryButton={false}
        >
          <img
            src={fireworks}
            alt=""
            style={{
              width: 192,
              height: 192,
              margin: '0 auto',
              display: 'block',
            }}
          />

          {/* Single FeedbackForm instance - always mounted */}
          <div
            id="inAppFeedbackRef-id"
            style={{
              width: '100%',
              maxWidth: '420px',
              minHeight: '280px',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <FeedbackForm
              uiType="inline"
              questionText={t('feedback.inline.question')}
              feedbackSource="thanks-screen"
              authenticationType="anonymous"
              onReady={handleFeedbackReady}
              onSubmitStart={handleFeedbackSubmitStart}
              onSuccess={handleFeedbackSuccess}
              onError={handleFeedbackError}
              onDismiss={handleFeedbackDismiss}
            />
          </div>

          <Text variant="small" style={{ textAlign: 'center', color: '#616161' }}>
            {t('completion.skip')}
          </Text>
        </CardScreen>
      </div>
    </>
  );
}